/**
 * Disable Organisation API functions
 *
 * Wraps the existing organisation/account/campaign/donation endpoints. The
 * disable action reuses the existing `DELETE /api/v2/organization/{id}` call;
 * no new backend endpoint is introduced.
 */

import constants from '../../../constants';
import {
  CampaignSearchItem,
  OrgAdmin,
  OrgDetails,
  OrgImpact,
  OrgLoadResult,
  SearchResponse
} from './types';
import {
  ADMIN_ORGANIZATION_ID_FILTER_KEY,
  buildOrgSearchRequest,
  sumRaisedAmount
} from './helpers';

const API_BASE = constants.baseApiHost;
const API_KEY = constants.apikey;

const getSession = () => {
  const localStorageSession = localStorage['session'];
  return localStorageSession && localStorageSession !== 'undefined'
    ? JSON.parse(localStorageSession)
    : null;
};

const getAuthHeaders = (): Record<string, string> => {
  const session = getSession();
  return {
    ApiKey: API_KEY || '',
    'Content-Type': 'application/json',
    Authorization: session?.key ? `bearer ${session.key}` : ''
  };
};

const handleResponse = async <T>(res: Response): Promise<T> => {
  if (!res.ok) {
    const errorText = await res.text().catch(() => res.statusText);
    throw new Error(`API ${res.status}: ${errorText}`);
  }
  return res.json();
};

const postSearch = async <T>(
  entity: string,
  version: 'v2' | 'v3',
  body: unknown
): Promise<SearchResponse<T>> => {
  const res = await fetch(`${API_BASE}/api/${version}/${entity}/search`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(body)
  });
  // The API wraps the result: { data: { list, totalCount, ... } }.
  const json = await handleResponse<{ data?: SearchResponse<T> }>(res);
  return json.data ?? {};
};

/**
 * GET a single organisation by id.
 */
export const getOrganization = async (id: number): Promise<OrgDetails> => {
  const res = await fetch(`${API_BASE}/api/v2/organization/${id}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  // The API wraps the entity: { data: { ...organization } }.
  const json = await handleResponse<{ data: OrgDetails }>(res);
  return json.data;
};

/**
 * API max page size; passing a larger value is silently capped by the API.
 */
const MAX_PAGE_SIZE = 75;

/** A sample page of admins is enough; the true count comes from `totalCount`. */
const ADMINS_PAGE_SIZE = 10;

/**
 * Load a page of admins for an organisation plus the total admin count.
 */
export const getOrganizationAdmins = async (
  id: number
): Promise<{ admins: OrgAdmin[]; totalCount: number }> => {
  const request = buildOrgSearchRequest(id, {
    pageSize: ADMINS_PAGE_SIZE,
    orgFilterKey: ADMIN_ORGANIZATION_ID_FILTER_KEY,
    orderBy: { key: 'firstName', direction: 'asc' }
  });
  const response = await postSearch<OrgAdmin>('account', 'v2', request);
  return { admins: response.list ?? [], totalCount: response.totalCount ?? 0 };
};

const CAMPAIGN_PAGE_SIZE = MAX_PAGE_SIZE;
const MAX_CAMPAIGN_PAGES = 25;

/**
 * Campaign count + total raised for an organisation. The count comes from
 * `totalCount`; the total raised is summed from each campaign's `raisedAmount`,
 * paging through results up to `MAX_CAMPAIGN_PAGES`. For an organisation with
 * more campaigns than that cap allows, the sum is a floor and `truncated` is
 * set so the UI can flag the total as partial.
 */
export const getCampaignSummary = async (
  id: number
): Promise<{ campaignCount: number; totalRaised: number; truncated: boolean }> => {
  let page = 1;
  let campaignCount = 0;
  let totalRaised = 0;
  let loaded = 0;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const request = buildOrgSearchRequest(id, {
      page,
      pageSize: CAMPAIGN_PAGE_SIZE,
      orderBy: { key: 'raisedAmount', direction: 'desc' }
    });
    const response = await postSearch<CampaignSearchItem>('page/campaign', 'v2', request);
    const list = response.list ?? [];
    campaignCount = response.totalCount ?? campaignCount;
    totalRaised += sumRaisedAmount(list);
    loaded += list.length;

    if (list.length < CAMPAIGN_PAGE_SIZE || loaded >= campaignCount || page >= MAX_CAMPAIGN_PAGES) {
      break;
    }
    page += 1;
  }

  return { campaignCount, totalRaised, truncated: loaded < campaignCount };
};

/**
 * Number of donations made to an organisation (count only).
 */
export const getDonationCount = async (id: number): Promise<number> => {
  // The v3 donation search requires filter values to be passed as an array.
  const request = buildOrgSearchRequest(id, { pageSize: 1, valueAsArray: true });
  const response = await postSearch<unknown>('donation', 'v3', request);
  return response.totalCount ?? 0;
};

/**
 * Load every piece of information the screen needs. The organisation itself is
 * required (a failure here rejects). The admins and impact figures load
 * independently: a failure on any of them is reported as `null` (so the UI can
 * show "Unavailable" rather than a misleading 0) and flagged via `incomplete`.
 */
export const loadOrganization = async (id: number): Promise<OrgLoadResult> => {
  const [detailsResult, adminsResult, campaignResult, donationResult] = await Promise.allSettled([
    getOrganization(id),
    getOrganizationAdmins(id),
    getCampaignSummary(id),
    getDonationCount(id)
  ]);

  if (detailsResult.status === 'rejected') {
    throw detailsResult.reason;
  }
  const details = detailsResult.value;

  const admins = adminsResult.status === 'fulfilled' ? adminsResult.value.admins : [];
  const adminCount = adminsResult.status === 'fulfilled' ? adminsResult.value.totalCount : null;

  const impact: OrgImpact = {
    campaignCount: campaignResult.status === 'fulfilled' ? campaignResult.value.campaignCount : null,
    donationCount: donationResult.status === 'fulfilled' ? donationResult.value : null,
    totalRaised: campaignResult.status === 'fulfilled' ? campaignResult.value.totalRaised : null,
    totalRaisedTruncated:
      campaignResult.status === 'fulfilled' ? campaignResult.value.truncated : false,
    currency: details.currency || 'AUD'
  };

  const incomplete = [adminsResult, campaignResult, donationResult].some(
    (result) => result.status === 'rejected'
  );

  return { details, admins, adminCount, impact, incomplete };
};

/**
 * Disable (permanently hide) an organisation via the existing DELETE endpoint.
 * Returns 204 No Content on success.
 */
export const disableOrganization = async (id: number): Promise<void> => {
  const res = await fetch(`${API_BASE}/api/v2/organization/${id}`, {
    method: 'DELETE',
    headers: getAuthHeaders()
  });

  if (res.status === 204 || res.ok) {
    return;
  }

  const errorText = await res.text().catch(() => res.statusText);
  throw new Error(`API ${res.status}: ${errorText}`);
};
